import styled from '@emotion/styled'
import { forwardRef, createElement, type HTMLAttributes } from 'react'

export type HeadingSize = 'display-xl' | 'display-lg' | 'display-md' | 'h1' | 'h2' | 'h3' | 'h4'
export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  size?: HeadingSize
  level?: HeadingLevel
}

const SIZE_TOKEN: Record<HeadingSize, string> = {
  'display-xl': 'var(--t-display-xl)',
  'display-lg': 'var(--t-display-lg)',
  'display-md': 'var(--t-display-md)',
  h1: 'var(--t-h1)',
  h2: 'var(--t-h2)',
  h3: 'var(--t-h3)',
  h4: 'var(--t-h4)',
}

const SIZE_TRACKING: Record<HeadingSize, string> = {
  'display-xl': '-0.03em',
  'display-lg': '-0.025em',
  'display-md': '-0.02em',
  h1: '-0.015em',
  h2: '-0.01em',
  h3: '-0.005em',
  h4: '0',
}

const StyledHeading = styled.h2<{ size: HeadingSize }>`
  font-family: var(--font-display);
  font-weight: 400;
  font-size: ${({ size }) => SIZE_TOKEN[size]};
  letter-spacing: ${({ size }) => SIZE_TRACKING[size]};
  line-height: 0.98;
  margin: 0;
  color: inherit;
`

// Heading renders the right HTML heading level (`level`) at the right
// visual scale (`size`). The two are independent so a page can be `<h1>`
// at display-md if that's the right semantics + visual hierarchy.
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(
  { size = 'h2', level = 2, ...rest },
  ref,
) {
  return createElement(StyledHeading, { as: `h${level}`, size, ref, ...rest })
})
